import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { resolveTarget } from "@/lib/announcement-targets";
import { useEffect, useState } from "react";

/**
 * Unified announcement banner.
 *
 * Reads a single active banner for the given (scope, academyId) and renders
 * it as a thin, tenant-safe strip above the page content.
 *
 * - Scopes:
 *   - platform_public         → public Dwrrah landing
 *   - platform_instructor     → inside instructor dashboard
 *   - academy_public          → academy public landing
 *   - academy_internal        → inside student-facing academy
 *
 * - Internal scopes only render `internal` CTAs (resolved via the safe
 *   target registry); external URLs are ignored even if present.
 * - Honors start_at/end_at on the client (RLS still applies).
 * - Manual dismissal persists in localStorage by id.
 */
export default function AnnouncementBanner({ scope, academy }) {
  const [item, setItem] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const academyId = academy?.id || null;
  const slug = academy?.slug || null;
  const needsAcademy = scope === "academy_public" || scope === "academy_internal";

  useEffect(() => {
    let alive = true;
    if (needsAcademy && !academyId) {
      setLoaded(true);
      return () => { alive = false; };
    }
    (async () => {
      let q = supabase
        .from("announcements")
        .select("*")
        .eq("scope", scope)
        .eq("is_enabled", true)
        .order("sort_order", { ascending: false })
        .order("created_at", { ascending: false })
        .limit(1);
      if (needsAcademy) q = q.eq("academy_id", academyId);
      else q = q.is("academy_id", null);
      const { data, error } = await q;
      if (!alive) return;
      if (error || !data || data.length === 0) {
        setItem(null);
      } else {
        const a = data[0];
        const now = Date.now();
        const startOk = !a.start_at || new Date(a.start_at).getTime() <= now;
        const endOk = !a.end_at || new Date(a.end_at).getTime() > now;
        setItem(startOk && endOk ? a : null);
      }
      setLoaded(true);
    })();
    return () => { alive = false; };
  }, [scope, academyId, needsAcademy]);

  const dismissKey = item ? `dwrrah:annc:dismissed:${item.id}` : null;
  useEffect(() => {
    if (!dismissKey) return;
    try { setDismissed(!!localStorage.getItem(dismissKey)); } catch {}
  }, [dismissKey]);

  if (!loaded || !item || dismissed) return null;

  const isInternalScope = scope === "platform_instructor" || scope === "academy_internal";

  // CTA resolution
  let ctaHref = null;
  let ctaExternal = false;
  if (item.cta_kind === "internal" && item.cta_internal_key) {
    ctaHref = resolveTarget(item.cta_internal_key, { slug });
  } else if (item.cta_kind === "external" && item.cta_external_url && !isInternalScope) {
    ctaHref = item.cta_external_url;
    ctaExternal = true;
  }

  const handleDismiss = () => {
    try { if (dismissKey) localStorage.setItem(dismissKey, "1"); } catch {}
    setDismissed(true);
  };

  return (
    <div
      className="relative z-30 w-full px-4 py-2.5 text-sm flex items-center justify-center gap-3 flex-wrap"
      style={{
        backgroundColor: item.bg_color || "#1f2937",
        color: item.text_color || "#ffffff",
      }}
      role="status"
    >
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-medium truncate sm:whitespace-normal">{item.title}</span>
      </div>
      {ctaHref && item.cta_label ? (
        ctaExternal ? (
          <a
            href={ctaHref}
            target="_blank"
            rel="noopener noreferrer"
            className="px-3 py-1 rounded-md text-xs font-semibold border border-current/30 hover:bg-white/10 transition-colors"
            style={{ borderColor: "currentColor" }}
          >
            {item.cta_label}
          </a>
        ) : (
          <Link
            to={ctaHref}
            className="px-3 py-1 rounded-md text-xs font-semibold border hover:bg-white/10 transition-colors"
            style={{ borderColor: "currentColor" }}
          >
            {item.cta_label}
          </Link>
        )
      ) : null}
      <button
        type="button"
        onClick={handleDismiss}
        className="ms-auto p-1 rounded hover:bg-white/10 transition-colors shrink-0"
        aria-label="Dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}